import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import Alert from "../../Common/Alert";
import Http from "../../Common/Http";
import IsLogin from "../../Common/IsLogin";
import { LoaderComponent } from "../../Common/LoaderComponent";

const ProductionWorkOrderItemReport = () => {
  const location = useLocation();
  const [alert, setAlert] = useState(null);

  const [inputs, setInputs] = useState({
    productionworkorder: "",
  });

  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4000);
  };

  useEffect(() => {
    if (!location.state) return;
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const fetchWorkOrders = async () => {
    try {
      const res = await Http.get("/production-work-order?show_all=0");
      setWorkOrders(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Failed to load work orders:", error);
      setWorkOrders([]);
    }
  };

  useEffect(() => {
    fetchWorkOrders();
  }, []);

  const validateInputs = () => {
    if (!inputs.productionworkorder) {
      Swal.fire({
        icon: "warning",
        title: "Missing input",
        text: "Please select a work order.",
      });
      return false;
    }
    return true;
  };

  const formSubmit = async () => {
    if (!validateInputs()) return;
    setLoading(true);
    setReport(null);
    try {
      const queryParams = new URLSearchParams({
        productionworkorder: inputs.productionworkorder,
      });
      const res = await Http.get(
        `/production-workorder-item-report?${queryParams.toString()}`
      );
      if (!res?.data?.data || res.data.data.length === 0) {
        setReport(res.data);
        Swal.fire({
          icon: "warning",
          title: "Oops...",
          text: "No data found!",
        });
        return;
      }
      setReport(res.data);
    } catch (error) {
      console.error("Report fetch error:", error);
      Swal.fire({
        icon: "error",
        title: "Request failed",
        text: "Failed to load report. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedWorkOrder = useMemo(() => {
    const id = Number(inputs.productionworkorder);
    if (!id) return null;
    return workOrders.find((w) => Number(w.id) === id) || null;
  }, [inputs.productionworkorder, workOrders]);

  const departments = useMemo(() => {
    const firstRowDepartments = Array.isArray(report?.data?.[0]?.departments)
      ? report.data[0].departments
      : [];
    const fromSummary = Array.isArray(report?.summary)
      ? report.summary
          .map((s) => s?.department)
          .filter(Boolean)
          .map((d) => ({ id: Number(d.id), name: d.name }))
      : [];
    const fromData = Array.isArray(report?.data)
      ? report.data
          .flatMap((r) => r?.departments || [])
          .filter(Boolean)
          .map((d) => ({ id: Number(d.id), name: d.name }))
      : [];
    const map = new Map();
    [...firstRowDepartments, ...fromData, ...fromSummary].forEach((d) => {
      if (!d?.id) return;
      if (!map.has(d.id)) map.set(d.id, d);
    });
    return Array.from(map.values());
  }, [report]);

  const summaryByDepartmentId = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(report?.summary)) return map;
    report.summary.forEach((s) => {
      const id = Number(s?.department?.id);
      if (!id) return;
      map.set(id, s?.totals || {});
    });
    return map;
  }, [report]);

  const metricDefs = useMemo(
    () => [
      { key: "quantity", label: "Qty", title: "Quantity" },
      { key: "a", label: "A", title: "A Class" },
      { key: "b", label: "B", title: "B Class" },
      { key: "c", label: "C", title: "C Class" },
      { key: "export", label: "Exp", title: "Export Quantity" },
    ],
    []
  );

  const qtyOnlyDepartmentIds = useMemo(() => new Set([2, 3, 4, 5, 6, 7]), []);
  const isQtyOnlyDepartment = (departmentId) =>
    qtyOnlyDepartmentIds.has(Number(departmentId));

  const groups = useMemo(
    () => [
      { key: "production", label: "Production", title: "Production metrics" },
      { key: "transfer_in", label: "Receive", title: "Transfer In (Receive) metrics" },
      { key: "transfer_out", label: "Transfer", title: "Transfer Out (Transfer) metrics" },
      { key: "loss", label: "Loss", title: "Loss metrics" },
    ],
    []
  );

  const metricsForDepartment = (departmentId) =>
    isQtyOnlyDepartment(departmentId) ? metricDefs.slice(0, 1) : metricDefs;

  const safeNumber = (value) => {
    if (typeof value === "number") return value;
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const cellClass = (groupKey, metricKey, value) => {
    const v = safeNumber(value);
    if (groupKey === "loss" && v > 0) return "text-danger fw-semibold";
    // if (metricKey === "export" && v > 0) return "text-primary fw-semibold";
    return "";
  };

  const parallelHoldPartnerById = useMemo(() => {
    const map = new Map();
    [
      [2, 3],
      [9, 10],
      [12, 13],
    ].forEach(([a, b]) => {
      map.set(a, b);
      map.set(b, a);
    });
    return map;
  }, []);

  const findFirstValidHoldDepartment = (departmentTimeline) => {
    if (!Array.isArray(departmentTimeline) || departmentTimeline.length === 0) {
      return null;
    }
    const indexByDepartmentId = new Map();
    departmentTimeline.forEach((d, index) => {
      indexByDepartmentId.set(Number(d?.id), index);
    });

    let holdTrueCount = 0;

    for (let index = 0; index < departmentTimeline.length; index++) {
      const department = departmentTimeline[index];
      const departmentId = Number(department?.id);
      if (!departmentId) continue;

      const partnerId = parallelHoldPartnerById.get(departmentId);
      let ignoreCurrentDepartment = false;

      if (partnerId) {
        const partnerIndex = indexByDepartmentId.get(partnerId);
        if (typeof partnerIndex === "number" && partnerIndex < index) {
          const partnerDepartment = departmentTimeline[partnerIndex];
          const currentHold = Boolean(department?.hold);
          const partnerHold = Boolean(partnerDepartment?.hold);
          if (currentHold !== partnerHold) {
            ignoreCurrentDepartment = true;
          }
        }
      }

      if (ignoreCurrentDepartment) continue;

      if (department?.hold === true) {
        holdTrueCount += 1;
        if (holdTrueCount === 1) {
          return department;
        }
      }
    }

    return null;
  };

  const rowModels = useMemo(() => {
    if (!Array.isArray(report?.data)) return [];
    return report.data.map((row, index) => {
      const item = row?.work_order_item || {};
      const sourceDepartments = Array.isArray(row?.departments)
        ? row.departments
        : [];
      const deptMap = new Map(sourceDepartments.map((d) => [Number(d.id), d]));
      const firstHoldDepartment = findFirstValidHoldDepartment(sourceDepartments);
      const itemName = item?.product?.name || "N/A";
      const itemCode = item?.product?.code ? ` (${item.product.code})` : "";
      return {
        key: item.id || index,
        index,
        item,
        deptMap,
        firstHoldDepartmentId: Number(firstHoldDepartment?.id) || null,
        firstHoldDepartmentName: firstHoldDepartment?.name || "",
        itemName,
        itemCode,
      };
    });
  }, [report, parallelHoldPartnerById]);

  return (
    <div>
      <IsLogin></IsLogin>
      <LoaderComponent />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Production Work Order Item Report</h5>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />
          <div className="card pd-20 pd-sm-40">
            <div className="row">
              <div className="col-lg">
                <label>Work Order</label>
                <select
                  className="form-control"
                  name="productionworkorder"
                  value={inputs.productionworkorder || ""}
                  onChange={handleChange}
                >
                  <option value="">Select Work Order</option>
                  {workOrders.map((wo) => (
                    <option key={wo.id} value={wo.id}>
                      {wo.receipt_no}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg mt-4">
                <button
                  className="btn btn-primary btn-block mg-b-10"
                  onClick={formSubmit}
                  disabled={loading}
                >
                  <i className="fa fa-send mg-r-10"></i> Search
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* {report?.meta && (
          <div className="am-pagebody">
            <div className="card pd-20 pd-sm-40">
              <div className="d-flex justify-content-between flex-wrap">
                <div>
                  <b>Report:</b> {report.meta.report}
                </div>
                <div>
                  <b>Work Order:</b>{" "}
                  {selectedWorkOrder?.receipt_no ||
                    report.meta.production_work_order_id}
                </div>
                <div>
                  <b>Generated:</b> {report.meta.generated_at}
                </div>
              </div>
            </div>
          </div>
        )} */}

        {report?.data && report.data.length > 0 && (
          <div className="am-pagebody">
            <div className="card pd-20 pd-sm-40">
              <h6 className="mg-b-10">Work Order Items</h6>

              <div
                className="erp-hier-table-container"
                role="region"
                aria-label="Work order item report table"
                tabIndex={0}
              >
                <table className="table table-sm table-bordered table-striped mb-0 erp-hier-table">
                  <caption className="visually-hidden">
                    Work order items by department with production, transfer in, transfer out and loss metrics
                  </caption>
                  <thead>
                    <tr className="text-center erp-hier-head-1">
                      <th rowSpan={3} scope="col">
                        #
                      </th>
                      <th rowSpan={3} scope="col" className="text-start erp-hier-wrap-150">
                        Item
                      </th>
                      <th rowSpan={3} scope="col" className="text-end">
                        Order Qty
                      </th>
                      <th rowSpan={3} scope="col" className="text-start erp-hier-wrap-150">
                        Shape
                      </th>
                      {/* <th rowSpan={3} scope="col" className="text-nowrap text-start">
                        Color
                      </th> */}
                      <th rowSpan={3} scope="col" className="text-start erp-hier-wrap-150">
                        Glaze
                      </th>
                      <th rowSpan={3} scope="col" className="text-start erp-hier-wrap-150">
                        Decoration
                      </th>
                      {departments.map((d) => (
                        <th
                          key={d.id}
                          colSpan={metricsForDepartment(d.id).length * groups.length}
                          scope="colgroup"
                          className="erp-hier-dept"
                          title={d.name}
                        >
                          {d.name}
                        </th>
                      ))}
                    </tr>
                    <tr className="text-center erp-hier-head-2">
                      {departments.map((d) => (
                        <React.Fragment key={d.id}>
                          {groups.map((g) => (
                            <th
                              key={`${d.id}-${g.key}`}
                              colSpan={metricsForDepartment(d.id).length}
                              scope="colgroup"
                              title={g.title}
                            >
                              {g.label}
                            </th>
                          ))}
                        </React.Fragment>
                      ))}
                    </tr>
                    <tr className="text-center erp-hier-head-3">
                      {departments.map((d) => (
                        <React.Fragment key={d.id}>
                          {groups.flatMap((g, groupIndex) =>
                            metricsForDepartment(d.id).map((m, metricIndex) => (
                              <th
                                key={`${d.id}-${g.key}-${m.key}`}
                                scope="col"
                                className={[
                                  metricIndex === 0 ? "erp-hier-group-start" : "",
                                  groupIndex === 0 && metricIndex === 0 ? "erp-hier-dept-start" : "",
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                                title={`${d.name} · ${
                                  g.label
                                } · ${m.title}`}
                              >
                                {m.label}
                              </th>
                            ))
                          )}
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rowModels.map((row) => {
                      const item = row.item || {};
                      return (
                        <tr key={row.key}>
                          <td className="text-center">{row.index + 1}</td>
                          <td className="text-start fw-semibold erp-hier-wrap-150">
                            {row.itemName}
                            {row.itemCode}
                            {row.firstHoldDepartmentName && (
                              <div
                                className="fw-normal text-danger"
                                style={{ fontSize: "12px" }}
                              >
                                Holded Department: {row.firstHoldDepartmentName}
                              </div>
                            )}
                          </td>
                          <td className="text-end">{safeNumber(item?.qty)}</td>
                          <td className="text-start erp-hier-wrap-150">
                            {item?.shape || "N/A"}
                          </td>
                          {/* <td className="text-nowrap text-start">{item?.color || "N/A"}</td> */}
                          <td className="text-start erp-hier-wrap-150">{item?.glaze || "N/A"}</td>
                          <td
                            className="text-start erp-hier-wrap-150"
                            title={item?.decoration_name || ""}
                          >
                            {item?.decoration_name || item?.decoration_code || "N/A"} ({item?.decoration_code || "N/A"})
                          </td>
                          {departments.flatMap((d) => {
                            const metrics = row.deptMap.get(Number(d.id)) || {};
                            const isHoldDepartment =
                              Number(d.id) === row.firstHoldDepartmentId;
                            const production = metrics?.production || {};
                            const transferIn = metrics?.transfer_in || {};
                            const transferOut = metrics?.transfer_out || {};
                            const loss = metrics?.loss || {};
                            const groupData = {
                              production,
                              transfer_in: transferIn,
                              transfer_out: transferOut,
                              loss,
                            };
                            const metricList = metricsForDepartment(d.id);
                            return groups.flatMap((g, groupIndex) =>
                              metricList.map((m, metricIndex) => {
                                const value = safeNumber(groupData?.[g.key]?.[m.key]);
                                const classes = [
                                  "text-end",
                                  cellClass(g.key, m.key, value),
                                  metricIndex === 0 ? "erp-hier-group-start" : "",
                                  groupIndex === 0 && metricIndex === 0 ? "erp-hier-dept-start" : "",
                                ]
                                  .filter(Boolean)
                                  .join(" ");
                                return (
                                  <td
                                    key={`${d.id}-${g.key}-${m.key}`}
                                    className={classes}
                                    style={
                                      isHoldDepartment
                                        ? { backgroundColor: "#f8d7da" }
                                        : undefined
                                    }
                                  >
                                    {value}
                                  </td>
                                );
                              })
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                  {departments.length > 0 && (
                    <tfoot>
                      <tr>
                        <th colSpan={6} className="text-end">
                          Total
                        </th>
                        {departments.flatMap((d) => {
                          const totals = summaryByDepartmentId.get(Number(d.id)) || {};
                          const production = totals?.production || {};
                          const transferIn = totals?.transfer_in || {};
                          const transferOut = totals?.transfer_out || {};
                          const loss = totals?.loss || {};
                          const groupData = {
                            production,
                            transfer_in: transferIn,
                            transfer_out: transferOut,
                            loss,
                          };
                          const metricList = metricsForDepartment(d.id);
                          return groups.flatMap((g, groupIndex) =>
                            metricList.map((m, metricIndex) => {
                              const value = safeNumber(groupData?.[g.key]?.[m.key]);
                              const classes = [
                                "text-end",
                                "fw-semibold",
                                cellClass(g.key, m.key, value),
                                metricIndex === 0 ? "erp-hier-group-start" : "",
                                groupIndex === 0 && metricIndex === 0 ? "erp-hier-dept-start" : "",
                              ]
                                .filter(Boolean)
                                .join(" ");
                              return (
                                <th key={`tot-${d.id}-${g.key}-${m.key}`} className={classes}>
                                  {value}
                                </th>
                              );
                            })
                          );
                        })}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductionWorkOrderItemReport;
